import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';
import EntityTable, { renderActionButton } from '../components/common/EntityTable';
import HighlightedText from '../components/common/HighlightedText';
import '../styles/components/bulk-actions.css';
import '../styles/components/ordenes-trabajo-list.css';
import '../styles/pages/equipos.css';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DocumentosEquipoList from '../components/documentos/DocumentosEquipoList';
import VersionHistoryModal from '../components/documentos/VersionHistoryModal';
import { useToast } from '../components/contexts/ToastContext';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import useEquipoDocumentos from '../hooks/useEquipoDocumentos';
import useEquiposBulkActions from '../hooks/useEquiposBulkActions';
import useEquipoOrdenesTrabajo from '../hooks/useEquipoOrdenesTrabajo';
import OrdenesTrabajoModal from '../components/equipos/OrdenesTrabajoModal';
import apiService from '../services/apiService';
import { useExportExcel } from '../hooks/useExportExcel';

// Tipos de problema para reportes (constante estática sin dependencias del render)
const TIPOS_PROBLEMA = [
  { value: 'incorrecto', label: 'Documento incorrecto' },
  { value: 'ilegible', label: 'Archivo ilegible o corrupto' },
  { value: 'equipo_erroneo', label: 'Pertenece a otro equipo' },
  { value: 'desactualizado', label: 'Documento desactualizado' },
  { value: 'otro', label: 'Otro problema' }
];

const ESTADO_BADGE_CLASS = {
  operativo: 'badge-success',
  en_mantenimiento: 'badge-warning',
  fuera_de_servicio: 'badge-danger',
  dado_de_baja: 'badge-secondary'
};

const ESTADO_CARD_CLASS = {
  operativo:        'status-operativo',
  en_mantenimiento: 'status-mantenimiento',
  fuera_de_servicio:'status-fuera',
  dado_de_baja:     'status-baja'
};

const ESTADO_LABEL = {
  operativo:        'Operativo',
  en_mantenimiento: 'En Mantenimiento',
  fuera_de_servicio:'Fuera de Servicio',
  dado_de_baja:     'Dado de Baja'
};

const ESTADO_OPTIONS = [
  { value: 'operativo',         label: 'Operativo' },
  { value: 'en_mantenimiento',  label: 'En Mantenimiento' },
  { value: 'fuera_de_servicio', label: 'Fuera de Servicio' },
  { value: 'dado_de_baja',      label: 'Dado de Baja' }
];

const BULK_ESTADO_ACTIONS = [
  { estado: 'operativo',        label: 'Marcar Operativo', icon: 'fa-check', variant: 'btn-success'   },
  { estado: 'en_mantenimiento', label: 'En Mantenimiento', icon: 'fa-tools', variant: 'btn-warning'   },
  { estado: 'dado_de_baja',     label: 'Dar de Baja',      icon: 'fa-ban',   variant: 'btn-secondary' },
];

const REPORTE_FIELDS = [
  { name: 'tipo_problema', label: 'Tipo de problema',         type: 'select',   required: true, options: TIPOS_PROBLEMA },
  { name: 'descripcion',   label: 'Descripción del problema', type: 'textarea', required: true, placeholder: 'Describe el problema encontrado...' }
];


/**
 * Página de gestión de equipos
 *
 * Permite CRUD de equipos, filtrado por estado/categoría, y gestión
 * de documentos asociados a través de un sidebar lateral.
 *
 * @returns {JSX.Element}
 */
function Equipos() {
  // 1. Hook CRUD core
  const {
    data: equipos,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem: editingEquipo,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm,
    refetch
  } = usePaginatedEntity({
    entityName: 'equipo',
    entityNamePlural: 'equipos',
    fetchFn: apiService.getEquipos,
    createFn: apiService.createEquipo,
    updateFn: apiService.updateEquipo,
    deleteFn: apiService.deleteEquipo,
    defaultFilters: { search: '', estado: '', categoria_equipo_id: '' },
    filterKeys: ['search'],
    messages: {
      createSuccess: 'Equipo creado',
      updateSuccess: 'Equipo actualizado',
      deleteSuccess: 'Equipo eliminado exitosamente'
    }
  });

  // 2. Contextos y auth
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  // 3. Catálogos simples
  const [categorias, setCategorias] = useState([]);

  // 4. Cómputos que dependen de data
  const displayedEquipos = equipos;

  // 6. Hooks de dominio (después de displayedEquipos)
  // deselectAll viene de useEquiposBulkActions — NO está en usePaginatedEntity
  const {
    selectedEquipos,
    bulkProcessing,
    toggleSelect, selectAll, deselectAll,
    bulkChangeEstado, bulkDelete
  } = useEquiposBulkActions({ displayedEquipos, refetch });

  const {
    selectedEquipoForDocs, setSelectedEquipoForDocs,
    uploadModalOpen,
    editingDoc,
    docLoading,
    docToDelete,
    versionHistoryOpen,
    docForHistory,
    reportModalOpen,
    docToReport,
    reportLoading,
    docsRefreshTrigger,
    isDragging,
    droppedFile,
    openUploadModal,
    openEditDocModal,
    openVersionHistory,
    closeUploadModal,
    closeVersionHistory,
    closeReportModal,
    requestDeleteDoc,
    cancelDeleteDoc,
    handleDeleteDoc,
    handleReportClick,
    handleReportSubmit,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleUploadSave,
    getDocumentoFields,
  } = useEquipoDocumentos();

  const {
    ordenesTrabajoModalOpen,
    selectedEquipoMant,
    ordenesTrabajo,
    otLoading,
    openOrdenesTrabajo,
    closeOrdenesTrabajo,
  } = useEquipoOrdenesTrabajo();

  /**
   * Carga catálogos de categorías de equipos
   * @returns {Promise<void>}
   */
  const fetchCatalogos = useCallback(async () => {
    try {
      const catRes = await apiService.getCategoriasLista();
      if (catRes.success) setCategorias(catRes.data);
    } catch (error) {
      console.error('Error fetching catalogos:', error);
    }
  }, []);

  useEffect(() => {
    fetchCatalogos();
  }, [fetchCatalogos]);

  // 7. Callbacks que dependen de deselectAll — DESPUÉS del bloque de hooks

  /**
   * Handler para filtros manuales (resetea selección)
   * @param {string} name - Nombre del filtro
   * @param {*} value - Valor del filtro
   */
  const handleFilterChangeWithSelection = useCallback((name, value) => {
    handleFilterChange(name, value);
    deselectAll();
  }, [handleFilterChange, deselectAll]);

  /**
   * Handler para paginación (resetea selección)
   * @param {number} newPage - Nueva página
   */
  const handlePageChangeWithSelection = useCallback((newPage) => {
    handlePageChange(newPage);
    // Resetear selección al cambiar página
    deselectAll();
  }, [handlePageChange, deselectAll]);

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (e) => ({
      'Código':          e.codigo,
      'Marca':           e.marca,
      'Modelo':          e.modelo,
      'Año':             e.año_equipo,
      'Categoría':       e.categoria?.nombre || '',
      'Estado':          e.estado.replace('_', ' '),
      'Documentos':      e.cantidad_documentos || 0,
      'Número de Serie': e.numero_serie || '',
      'Notas':           e.notas || ''
    }),
    sheetName: 'Equipos',
    filePrefix: 'equipos_',
    columnWidths: [
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 8 },
      { wch: 20 }, { wch: 18 }, { wch: 12 }, { wch: 20 }, { wch: 30 }
    ],
    entityLabel: 'equipos'
  });

  /**
   * Renderer personalizado de card mobile para equipos.
   * Reemplaza el card genérico de EntityTable con el diseño del mockup:
   * header oscuro con código/estado, grid de specs y botones etiquetados.
   *
   * @param {Object} row - Fila de equipo
   * @param {number} index - Índice de la fila
   * @param {Array} visibleActions - Acciones visibles filtradas por permisos
   * @returns {JSX.Element}
   */
  const renderEquipoMobileCard = useCallback((row, index, visibleActions) => (
    <div key={row.id} className={`mobile-card equipo-card ${ESTADO_CARD_CLASS[row.estado] || ''}`}>

      <div className="mobile-card-header">
        <span className="mobile-card-title">
          <HighlightedText text={row.codigo} searchTerm={filters.search} />
        </span>
        <span className={`badge ${ESTADO_BADGE_CLASS[row.estado]}`}>
          <HighlightedText text={ESTADO_LABEL[row.estado]} searchTerm={filters.search} />
        </span>
      </div>

      <div className="eq-specs">
        <div className="eq-spec">
          <span className="mobile-card-label">Marca</span>
          <span className="mobile-card-value">
            <HighlightedText text={row.marca} searchTerm={filters.search} />
          </span>
        </div>
        <div className="eq-spec">
          <span className="mobile-card-label">Modelo</span>
          <span className="mobile-card-value">
            <HighlightedText text={row.modelo} searchTerm={filters.search} />
          </span>
        </div>
        <div className="eq-spec">
          <span className="mobile-card-label">Año</span>
          <span className="mobile-card-value">{row.año_equipo}</span>
        </div>
      </div>

      <div className="mobile-card-body">
        <div className="mobile-card-field">
          <span className="mobile-card-label">
            <i className="fas fa-tag"></i> Categoría
          </span>
          <span className="mobile-card-value">
            {row.categoria?.nombre
              ? <HighlightedText text={row.categoria.nombre} searchTerm={filters.search} />
              : '-'}
          </span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label">
            <i className="fas fa-file-alt"></i> Documentos
          </span>
          <span className="mobile-card-value">{row.cantidad_documentos || 0}</span>
        </div>
      </div>

      {visibleActions?.length > 0 && (
        <div className="card-actions">
          {visibleActions.map(action =>
            renderActionButton(action, row, action.className?.includes('danger') ? 'btn-table-action' : 'btn-sm')
          )}
        </div>
      )}

    </div>
  ), [filters.search]);

  // 8. Cómputos que dependen de estado del bulk hook
  const columns = useMemo(() => [
    {
      key: 'select',
      mobileHidden: true,
      label: (
        <input
          type="checkbox"
          className="form-checkbox"
          checked={selectedEquipos.size === displayedEquipos.length && displayedEquipos.length > 0}
          onChange={(e) => e.target.checked ? selectAll() : deselectAll()}
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          className="form-checkbox"
          checked={selectedEquipos.has(row.id)}
          onChange={() => toggleSelect(row.id)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: '50px'
    },
    { key: 'codigo', label: 'Codigo', sortable: true, highlightable: true, title: true },
    { key: 'marca', label: 'Marca', sortable: true, highlightable: true },
    { key: 'modelo', label: 'Modelo', sortable: true, highlightable: true },
    { key: 'año_equipo', label: 'Año', sortable: true, highlightable: true },
    {
      key: 'categoria',
      label: 'Categoria',
      render: (row, searchTerm) => row.categoria?.nombre
        ? <HighlightedText text={row.categoria.nombre} searchTerm={searchTerm} />
        : '-'
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (row, searchTerm) => (
        <span className={`badge ${ESTADO_BADGE_CLASS[row.estado]}`}>
          <HighlightedText text={row.estado?.replace(/_/g, ' ')} searchTerm={searchTerm} />
        </span>
      )
    },
    {
      key: 'cantidad_documentos',
      label: 'Docs subidos',
      render: (row) => row.cantidad_documentos || 0
    },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], [selectedEquipos, displayedEquipos, selectAll, deselectAll, toggleSelect]);

  const modalFields = useMemo(() => [
    { name: 'codigo', label: 'Codigo', type: 'text', required: true },
    { name: 'marca', label: 'Marca', type: 'text', required: true },
    { name: 'modelo', label: 'Modelo', type: 'text', required: true },
    {
      name: 'año_equipo',
      label: 'Año del Equipo',
      type: 'number',
      required: true,
      min: 1900,
      max: new Date().getFullYear() + 5
    },
    { name: 'numero_serie', label: 'Numero de Serie', type: 'text' },
    {
      name: 'categoria_equipo_id',
      label: 'Categoria',
      type: 'select',
      required: true,
      options: categorias.map(c => ({ value: c.id, label: c.nombre }))
    },
    { name: 'estado', label: 'Estado', type: 'select', options: ESTADO_OPTIONS },
    { name: 'notas', label: 'Notas', type: 'textarea' }
  ], [categorias]);

  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por codigo, marca, modelo, categoría, estado...',
      icon: 'fa-search'
    }
  ], []);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Equipos' }
        ]} />

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChangeWithSelection}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nuevo Equipo',   icon: 'fa-plus',       variant: 'primary',   onClick: handleCreate, show: () => hasPermission('equipos.create') }
          ]}
        />

        {selectedEquipos.size > 0 && (hasPermission('equipos.update') || hasPermission('equipos.delete')) && (
          <div className="bulk-actions-bar">
            <div className="bulk-info">
              <i className="fas fa-check-square"></i>
              <strong>{selectedEquipos.size}</strong> equipo(s) seleccionado(s)
            </div>

            <div className="bulk-buttons">
              {hasPermission('equipos.update') && BULK_ESTADO_ACTIONS.map(({ estado, label, icon, variant }) => (
                <button
                  key={estado}
                  className={`btn btn-sm ${variant}`}
                  onClick={() => bulkChangeEstado(estado)}
                  disabled={bulkProcessing}
                >
                  <i className={`fas ${icon}`}></i> {label}
                </button>
              ))}

              {hasPermission('equipos.delete') && (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={bulkDelete}
                  disabled={bulkProcessing}
                >
                  <i className="fas fa-trash"></i> Eliminar
                </button>
              )}

              <button
                className="btn btn-sm btn-secondary"
                onClick={deselectAll}
              >
                <i className="fas fa-times"></i> Cancelar
              </button>
            </div>
          </div>
        )}

        <EntityTable
          columns={columns}
          data={displayedEquipos}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No se encontraron equipos"
          animateRows={true}
          mobileCardRenderer={renderEquipoMobileCard}
          actions={[
            { icon: 'fa-folder',  className: 'btn-ghost-info',    title: 'Ver documentos',         label: 'Docs',   onClick: setSelectedEquipoForDocs, show: () => hasPermission('documentos_equipo.read') },
            { icon: 'fa-history', className: 'btn-ghost-warning', title: 'Ver órdenes de trabajo', label: 'OTs',    onClick: openOrdenesTrabajo,       show: () => hasPermission('ordenes_trabajo.read_all') },
            { icon: 'fa-edit',    className: 'btn-ghost-primary', title: 'Editar',                 label: 'Editar', onClick: handleEdit,               show: () => hasPermission('equipos.update') },
            { icon: 'fa-trash',   className: 'btn-ghost-danger',  title: 'Eliminar',                                onClick: handleDelete,             show: () => hasPermission('equipos.delete') },
          ]}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChangeWithSelection}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          title={editingEquipo ? 'Editar Equipo' : 'Nuevo Equipo'}
          fields={modalFields}
          initialData={editingEquipo}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Equipo"
          message={`¿Esta seguro de eliminar el equipo "${confirmDialog.item?.codigo}"?`}
          confirmText="Eliminar"
          variant="danger"
        />
      </div>

      {/* Sidebar de Documentos */}
      {selectedEquipoForDocs && (
        <>
          <div
            className="sidebar-overlay"
            onClick={() => setSelectedEquipoForDocs(null)}
          />
          <div
            className={`sidebar-responsive-lg active bg-sidebar ${isDragging ? 'dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {isDragging && (
              <div className="drop-overlay">
                <div className="drop-indicator">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <p>Suelta los archivos aquí</p>
                  <small>PDF, JPG, PNG (máx 5MB)</small>
                </div>
              </div>
            )}

            <div className="sidebar-header">
              <div className="sidebar-header-content">
                <h3 className="sidebar-title">
                  <i className="fas fa-file-alt"></i> Documentos: {selectedEquipoForDocs.codigo}
                </h3>
                <p className="sidebar-subtitle">Documentos del equipo</p>
              </div>
              <button
                className="sidebar-close"
                onClick={() => setSelectedEquipoForDocs(null)}
                aria-label="Cerrar panel de documentos"
                title="Cerrar"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <DocumentosEquipoList
              equipoId={selectedEquipoForDocs.id}
              onUploadClick={hasPermission('documentos_equipo.upload') ? openUploadModal : undefined}
              onUpdateClick={hasPermission('documentos_equipo.update') ? openEditDocModal : undefined}
              onDeleteRequest={hasPermission('documentos_equipo.delete') ? requestDeleteDoc : undefined}
              onHistoryClick={hasPermission('documentos_equipo.read') ? openVersionHistory : undefined}
              onReportClick={hasPermission('reportes_documento.create') ? handleReportClick : undefined}
              canDownload={hasPermission('documentos_equipo.download')}
              refreshTrigger={docsRefreshTrigger}
            />
          </div>
        </>
      )}

      {/* Modal de confirmación para documentos - FUERA del sidebar */}
      <ConfirmDialog
        isOpen={!!docToDelete}
        onCancel={cancelDeleteDoc}
        onConfirm={handleDeleteDoc}
        title="Eliminar Documento"
        message="¿Está seguro de eliminar este documento? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      <EntityModal
        isOpen={uploadModalOpen}
        onClose={closeUploadModal}
        onSave={handleUploadSave}
        title={editingDoc ? 'Actualizar Documento' : (droppedFile ? `Subir: ${droppedFile.name}` : 'Subir Documento')}
        fields={getDocumentoFields()}
        initialData={editingDoc ? {
          tipo_documento_id: editingDoc.tipo_documento_id,
          fecha_vencimiento: editingDoc.fecha_vencimiento,
          archivo_nombre: editingDoc.archivo_path?.split('/').pop()
        } : (droppedFile ? {
          archivo_nombre: droppedFile.name
        } : null)}
        loading={docLoading}
      />

      <VersionHistoryModal
        isOpen={versionHistoryOpen}
        onClose={closeVersionHistory}
        documento={docForHistory}
      />

      <EntityModal
        isOpen={reportModalOpen}
        onClose={closeReportModal}
        onSave={handleReportSubmit}
        title={`Reportar Problema - ${docToReport?.archivo_path?.split('/').pop() || ''}`}
        fields={REPORTE_FIELDS}
        loading={reportLoading}
      />

      <OrdenesTrabajoModal
        isOpen={ordenesTrabajoModalOpen}
        equipo={selectedEquipoMant}
        ordenesTrabajo={ordenesTrabajo}
        loading={otLoading}
        onClose={closeOrdenesTrabajo}
      />
    </>
  );
}

export default Equipos;
